import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../../i18n';

const statusLabels = (): Record<string, string> => ({
  draft: t('events.status.draft'), published: t('events.status.published'),
  completed: t('events.status.completed'), cancelled: t('events.status.cancelled'),
});

export interface EventFormValue {
  name: string; type_id: string; status: string;
  start_date: string; end_date: string;
  capacity: string | number; description: string;
  visibility: string; tag_ids: string[];
  details_md: string | null; details_filename: string | null;
}

const BLANK: EventFormValue = {
  name: '', type_id: '', status: 'draft', start_date: '', end_date: '',
  capacity: '', description: '', visibility: 'all', tag_ids: [],
  details_md: null, details_filename: null,
};

/**
 * <cadence-event-form-modal> — the shared create/edit event form. The host owns the
 * API call: it passes initial `value`, `types`, and `tags` (all tags, for tag-scoped
 * visibility), and listens for `save` (detail = the collected form values) and `close`.
 * The internal working copy resets from `value` each time the modal opens.
 */
@customElement('cadence-event-form-modal')
export class CadenceEventFormModal extends LitElement {
  @property({ type: Boolean }) open = false;
  @property() heading = t('events.form.defaultHeading');
  @property({ attribute: 'submit-label' }) submitLabel = t('common.actions.save');
  @property({ type: Array }) types: any[] = [];
  @property({ type: Array }) tags: any[] = [];
  @property({ type: Object }) value: Partial<EventFormValue> = {};
  @property({ type: Boolean }) saving = false;
  @property() error = '';

  @state() private form: EventFormValue = { ...BLANK };

  static styles = css`
    :host { display: contents; }
    .form { display: flex; flex-direction: column; gap: var(--space-3); }
    .form-error { color: var(--color-danger); font-size: 0.85rem; margin: 0; }
    .two { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .two > * { min-width: 0; }
    .form cadence-input, .form cadence-select { display: block; width: 100%; }
    .ta-field { display: flex; flex-direction: column; gap: 6px; }
    .ta-field > span, .group > .lbl { font-size: 0.85rem; font-weight: 600; color: var(--color-text-secondary); }
    .ta-field textarea { font: inherit; padding: 10px 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg-primary); color: var(--color-text-primary); width: 100%; box-sizing: border-box; resize: vertical; min-height: 90px; }
    .ta-field textarea:focus { outline: 2px solid var(--color-primary); outline-offset: -1px; border-color: var(--color-primary); }
    .group { display: flex; flex-direction: column; gap: 8px; padding: 12px; border: 1px solid var(--color-border); border-radius: var(--radius-md); }
    .chips { display: flex; flex-wrap: wrap; gap: 8px; }
    .chip { font: inherit; cursor: pointer; border: 1px solid var(--color-border); background: var(--color-bg-primary); color: var(--color-text-secondary); padding: 5px 12px; border-radius: 999px; display: inline-flex; align-items: center; gap: 6px; }
    .chip.on { background: var(--color-primary); color: #fff; border-color: var(--color-primary); }
    .chip .dot { width: 9px; height: 9px; border-radius: 50%; }
    .muted { color: var(--color-text-secondary); font-size: 0.82rem; margin: 0; }
  `;

  updated(changed: Map<string, unknown>) {
    // Reset the working copy from `value` whenever the modal opens.
    if (changed.has('open') && this.open) {
      this.form = { ...BLANK, ...this.value, tag_ids: [...(this.value.tag_ids ?? [])] };
    }
  }

  private set(patch: Partial<EventFormValue>) { this.form = { ...this.form, ...patch }; }
  private emit(name: string, detail?: unknown) { this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true })); }

  private toggleTag(id: string) {
    const has = this.form.tag_ids.includes(id);
    this.set({ tag_ids: has ? this.form.tag_ids.filter((tid) => tid !== id) : [...this.form.tag_ids, id] });
  }

  render() {
    const f = this.form;
    return html`
      <cadence-modal ?open=${this.open} heading=${this.heading} @close=${() => this.emit('close')}>
        <div class="form">
          ${this.error ? html`<p class="form-error" role="alert">${this.error}</p>` : ''}
          <cadence-input label=${t('events.form.name')} .value=${f.name} @input=${(e: CustomEvent) => this.set({ name: e.detail })}></cadence-input>
          <div class="two">
            <cadence-select label=${t('events.form.type')} placeholder=${t('events.form.typePlaceholder')} .value=${f.type_id}
              .options=${this.types.map((ty) => ({ value: ty.id, label: ty.name }))}
              @change=${(e: CustomEvent) => this.set({ type_id: e.detail })}></cadence-select>
            <cadence-select label=${t('events.form.status')} .value=${f.status}
              .options=${Object.entries(statusLabels()).map(([value, label]) => ({ value, label }))}
              @change=${(e: CustomEvent) => this.set({ status: e.detail })}></cadence-select>
          </div>
          <div class="two">
            <cadence-input label=${t('events.form.startDate')} type="date" .value=${f.start_date} @input=${(e: CustomEvent) => this.set({ start_date: e.detail })}></cadence-input>
            <cadence-input label=${t('events.form.endDate')} type="date" .value=${f.end_date} @input=${(e: CustomEvent) => this.set({ end_date: e.detail })}></cadence-input>
          </div>
          <cadence-input label=${t('events.form.capacity')} type="number" .value=${String(f.capacity ?? '')} @input=${(e: CustomEvent) => this.set({ capacity: e.detail })}></cadence-input>
          <label class="ta-field"><span>${t('events.form.shortDescription')}</span>
            <textarea rows="3" .value=${f.description} @input=${(e: Event) => this.set({ description: (e.target as HTMLTextAreaElement).value })}></textarea>
          </label>

          <div class="group">
            <span class="lbl">${t('events.form.visibility')}</span>
            <cadence-select .value=${f.visibility}
              .options=${[{ value: 'all', label: t('events.form.visibilityAll') }, { value: 'tags', label: t('events.form.visibilityTags') }]}
              @change=${(e: CustomEvent) => this.set({ visibility: e.detail })}></cadence-select>
            ${f.visibility === 'tags' ? html`
              ${this.tags.length ? html`
                <div class="chips">
                  ${this.tags.map((tag) => html`
                    <button type="button" class="chip ${f.tag_ids.includes(tag.id) ? 'on' : ''}" @click=${() => this.toggleTag(tag.id)}>
                      <span class="dot" style="background:${tag.color}"></span>${tag.name}
                    </button>`)}
                </div>
                ${!f.tag_ids.length ? html`<p class="muted">${t('events.form.tagWarning')}</p>` : ''}
              ` : html`<p class="muted">${t('events.form.noTags')}</p>`}
            ` : ''}
          </div>

          <p class="muted">${t('events.form.detailsNotePre')}<strong>${t('events.tabs.details')}</strong>${t('events.form.detailsNotePost')}</p>
        </div>
        <div slot="footer">
          <cadence-button variant="ghost" @click=${() => this.emit('close')}>${t('common.actions.cancel')}</cadence-button>
          <cadence-button ?loading=${this.saving} @click=${() => this.emit('save', this.form)}>${this.submitLabel}</cadence-button>
        </div>
      </cadence-modal>
    `;
  }
}
